import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { CAPTCHA_BYPASS_TOKEN } from '@/constants/captcha';
import { useCaptchaBypass } from '@/hooks/use-captcha-bypass';

import Turnstile from './turnstile';

interface CaptchaProviderProps {
  onChange?: (token: string | null) => void;
  onExpired?: () => void;
  onError?: () => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
  resetRef?: MutableRefObject<(() => void) | null>;
  // SCRUM-1576: opt-out do bypass por país (RU/CN/TR). Forms sensíveis (ex.: redeem de cupom)
  // marcam para sempre exigir CAPTCHA, independentemente da feature toggle.
  disableBypass?: boolean;
}

export default function CaptchaProvider({ onChange, onExpired, onError, theme = 'light', size = 'normal', resetRef, disableBypass = false }: CaptchaProviderProps) {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { bypassCaptcha, isResolving } = useCaptchaBypass({ disabled: disableBypass });
  const hasEmittedBypassToken = useRef(false);

  const provider = process.env.NEXT_PUBLIC_CAPTCHA_PROVIDER || 'recaptcha';
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY || '';
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

  const resetCaptcha = useCallback(() => {
    if (provider === 'turnstile') {
      // Turnstile reset is handled globally through window.turnstileReset
      if (typeof window !== 'undefined' && (window as any).turnstileReset) {
        (window as any).turnstileReset();
      }
    } else if (recaptchaRef.current) {
      // reCAPTCHA reset
      recaptchaRef.current.reset();
    }
  }, [provider]);

  // Expose reset function to parent via ref (per-instance, supports multiple captchas on same page)
  useEffect(() => {
    if (!resetRef) return undefined;
    const ref = resetRef;
    ref.current = resetCaptcha;
    return () => {
      if (ref.current === resetCaptcha) ref.current = null;
    };
  }, [resetCaptcha, resetRef]);

  // Expose reset function globally for backward compatibility (last-mounted instance wins)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).captchaReset = resetCaptcha;
      // Keep backward compatibility with existing grecaptcha.reset calls
      if (!window.grecaptcha) {
        (window as any).grecaptcha = {
          reset: resetCaptcha,
        };
      } else if (window.grecaptcha && !window.grecaptcha.reset) {
        window.grecaptcha.reset = resetCaptcha;
      }
    }
  }, [resetCaptcha]);

  // SCRUM-1576: em países isentos não exibimos o widget e emitimos uma única vez um token
  // sentinela para satisfazer a validação `recaptcha.required()` dos formulários.
  useEffect(() => {
    if (!bypassCaptcha || hasEmittedBypassToken.current) return undefined;
    hasEmittedBypassToken.current = true;
    onChange?.(CAPTCHA_BYPASS_TOKEN);
    return undefined;
  }, [bypassCaptcha, onChange]);

  // Aguardando a resposta da toggle para um país isento: não renderiza o widget para evitar
  // exibi-lo e removê-lo logo em seguida (flicker).
  if (isResolving) {
    return null;
  }

  if (bypassCaptcha) {
    return null;
  }

  if (provider === 'turnstile') {
    return <Turnstile sitekey={turnstileSiteKey} onChange={onChange} onExpired={onExpired} onError={onError} theme={theme === 'dark' ? 'dark' : 'light'} size={size} />;
  }

  // Default to reCAPTCHA
  return <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaSiteKey} onChange={onChange} onExpired={onExpired} theme={theme} size={size} />;
}
